/**
 * Miles & More: Kartenfeld im Warenkorb und im Kundenkonto (Plan Abschnitt 2 und 6, AP8).
 *
 * <mm-kartenfeld> (snippets/mm-kartenfeld.liquid, Drawer und Warenkorbseite, als Leiste auch über dem
 * Produktraster der Kollektionen, AP10b F12):
 *   Prüft die Nummer erst im Browser auf das Format (T02: sonst kein Aufruf beim Worker), dann über
 *   den App-Proxy (POST /apps/meilen/pruefen) und speichert sie danach als Warenkorb-Attribut.
 *   Shopify übernimmt das Attribut in die Bestellung. Ist die Prüfung nicht erreichbar, wird die
 *   formal richtige Nummer trotzdem übernommen; der Worker prüft bei Zahlung ohnehin erneut.
 *   Leiste und Produktseite (data-mm-drawer-neu) lassen danach den Warenkorb-Drawer des Themes neu laden
 *   (cart:refresh), damit er die Nummer gleich zeigt, und klappen zu (data-mm-zuklappen).
 *   data-mm-aktiv (Nummer im Warenkorb oder im Kundenkonto) blendet per CSS um, was erst mit Nummer
 *   erscheint (Meilenzahl und Stufen im Warenkorb, Mockup 05.10.2026). Das Ereignis mm:karte gleicht
 *   andere Kartenfelder derselben Seite an; es trägt nur die maskierte Nummer.
 * <mm-kundenkonto> (snippets/mm-kundenkonto.liquid, Kontoseite):
 *   Speichert die Nummer im Kundenkonto (POST /apps/meilen/speichern) oder löscht sie (…/loeschen).
 *
 * Die volle Nummer steht nur im Eingabefeld und im Warenkorb-Attribut; angezeigt wird sie nur
 * maskiert (•••• 1933). Alle sichtbaren Texte stehen in den Snippets (data-text-*).
 * Antworten des App-Proxys: worker/src/shopify/proxy.ts.
 */

/** Gleiche Namen wie im Worker (worker/src/shopify/zugriff.ts) */
export const ATTRIBUT_KARTE = 'Miles & More Servicekarte';
export const ATTRIBUT_SPEICHERN = 'Miles & More speichern';

/** Miles-&-More-Nummern haben laut qiibee-Doku (List connections) 9–16 Ziffern. */
export const MIN_ZIFFERN = 9;
export const MAX_ZIFFERN = 16;

const PROXY = '/apps/meilen';

/** Ergebnisse, die als Erfolg angezeigt werden; alles andere als Fehler */
const ERFOLGE = ['gueltig', 'ungeprueft', 'entfernt', 'gespeichert', 'geloescht'];

/**
 * Entfernt Leerzeichen. Nur Ziffern in erlaubter Länge, sonst null.
 * @param {unknown} eingabe
 * @returns {string | null}
 */
export function normiereKarte(eingabe) {
  if (typeof eingabe !== 'string') return null;
  const ziffern = eingabe.replace(/\s+/g, '');
  if (!/^\d+$/.test(ziffern)) return null;
  return ziffern.length >= MIN_ZIFFERN && ziffern.length <= MAX_ZIFFERN ? ziffern : null;
}

/**
 * Nummer und Häkchen „speichern“ aus der Antwort von /cart.js. Im Live-Shop sieht Liquid das
 * Warenkorb-Attribut beim Rendern nicht (22.10.2026), das Kartenfeld liest den Stand deshalb selbst nach.
 * @param {unknown} warenkorb
 * @returns {{ karte: string, speichern: boolean } | null}
 */
export function karteImWarenkorb(warenkorb) {
  const w = /** @type {{ attributes?: Record<string, unknown> }} */ (typeof warenkorb === 'object' && warenkorb !== null ? warenkorb : {});
  const attribute = w.attributes ?? {};
  const karte = normiereKarte(attribute[ATTRIBUT_KARTE]);
  if (!karte) return null;
  return { karte, speichern: String(attribute[ATTRIBUT_SPEICHERN] ?? '').trim().toLowerCase() === 'ja' };
}

/**
 * Wie maskiereKarte im Worker (worker/src/karte.ts).
 * @param {string} nummer
 * @returns {string}
 */
export function maskiereKarte(nummer) {
  const ziffern = String(nummer).replace(/\D/g, '');
  return ziffern.length >= 4 ? `•••• ${ziffern.slice(-4)}` : '••••';
}

/**
 * Deutet die Antwort des App-Proxys.
 * @param {'pruefen' | 'speichern' | 'loeschen'} aktion
 * @param {number} status HTTP-Status, 0 bei Netzwerkfehler
 * @param {unknown} inhalt JSON-Antwort oder null
 * @returns {'gueltig' | 'ungueltig' | 'gespeichert' | 'geloescht' | 'nicht_angemeldet' | 'zu_viele' | 'aus' | 'nicht_erreichbar'}
 */
export function werteAntwortAus(aktion, status, inhalt) {
  const o = /** @type {Record<string, unknown>} */ (typeof inhalt === 'object' && inhalt !== null ? inhalt : {});
  if (status === 200) {
    if (aktion === 'loeschen') return o.geloescht === true ? 'geloescht' : 'nicht_erreichbar';
    if (o.gueltig === false) return 'ungueltig';
    if (o.gueltig !== true) return 'nicht_erreichbar';
    if (aktion === 'pruefen') return 'gueltig';
    return o.gespeichert === true ? 'gespeichert' : 'nicht_erreichbar';
  }
  if (status === 401 && o.fehler === 'nicht_angemeldet') return 'nicht_angemeldet';
  if (status === 429) return 'zu_viele';
  if (status === 503 && o.fehler === 'aus') return 'aus';
  return 'nicht_erreichbar';
}

/**
 * @param {'pruefen' | 'speichern' | 'loeschen'} aktion
 * @param {Record<string, string>} daten
 */
async function rufeProxy(aktion, daten) {
  let antwort;
  try {
    antwort = await fetch(`${PROXY}/${aktion}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(daten),
      credentials: 'same-origin',
    });
  } catch {
    return werteAntwortAus(aktion, 0, null);
  }
  let inhalt = null;
  try {
    inhalt = await antwort.json();
  } catch {
    // Keine JSON-Antwort, z. B. eine Fehlerseite von Shopify
  }
  return werteAntwortAus(aktion, antwort.status, inhalt);
}

/**
 * Setzt Warenkorb-Attribute (Ajax API). Ein leerer Wert entfernt das Attribut.
 * @param {Record<string, string>} attribute
 */
async function setzeAttribute(attribute) {
  const wurzel = window.Shopify?.routes?.root ?? '/';
  const antwort = await fetch(`${wurzel}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: attribute }),
    credentials: 'same-origin',
  });
  if (!antwort.ok) throw new Error(`cart/update.js ${antwort.status}`);
}

function definiereElemente() {
  class MmBasis extends HTMLElement {
    /** @param {Record<string, (ziel?: Element) => void>} aktionen */
    verbinde(aktionen) {
      if (this.dataset.mmBereit) return;
      this.dataset.mmBereit = 'ja';

      this.addEventListener('click', (e) => {
        const knopf = e.target instanceof Element ? e.target.closest('[data-mm-aktion]') : null;
        if (!knopf || !this.contains(knopf)) return;
        e.preventDefault();
        aktionen[knopf.getAttribute('data-mm-aktion') ?? '']?.();
      });

      // Enter im Feld würde sonst das Warenkorb-Formular absenden, also zum Checkout springen.
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || !(e.target instanceof Element) || !e.target.matches('[data-mm-eingabe]')) return;
        e.preventDefault();
        aktionen.enter?.();
      });

      // Eigene Eingaben nicht an Formular-Handler des Themes weitergeben (Warenkorb neu laden o. Ä.).
      this.addEventListener('input', (e) => e.stopPropagation());
      this.addEventListener('change', (e) => {
        e.stopPropagation();
        if (e.target instanceof Element) aktionen.aenderung?.(e.target);
      });
    }

    get eingabe() {
      return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-mm-eingabe]'));
    }

    get beschaeftigt() {
      return this.hasAttribute('aria-busy');
    }

    set beschaeftigt(ja) {
      this.toggleAttribute('aria-busy', ja);
      this.querySelectorAll('button').forEach((knopf) => {
        knopf.disabled = ja;
      });
    }

    /**
     * 'konto': Nummer nur im Kundenkonto gespeichert; dort ist das Häkchen „speichern“ überflüssig (Christian 08.10.).
     * @param {'eingabe' | 'fertig' | 'konto'} ansicht
     */
    zeige(ansicht) {
      this.querySelectorAll('[data-mm-ansicht]').forEach((el) => {
        /** @type {HTMLElement} */ (el).hidden = el.getAttribute('data-mm-ansicht') !== ansicht;
      });
      this.querySelectorAll('[data-mm-haken]').forEach((el) => {
        /** @type {HTMLElement} */ (el).hidden = ansicht === 'konto';
      });
    }

    /** @param {string} karte volle Nummer, angezeigt wird nur die maskierte */
    zeigeKarte(karte) {
      this.querySelectorAll('[data-mm-maskiert]').forEach((el) => {
        el.textContent = maskiereKarte(karte);
      });
      if (this.eingabe) this.eingabe.value = '';
      this.zeige('fertig');
    }

    /** @param {string} ergebnis Schlüssel des Textes in data-text-<ergebnis> */
    melde(ergebnis) {
      const ziel = /** @type {HTMLElement | null} */ (this.querySelector('[data-mm-meldung]'));
      if (!ziel) return;
      const erfolg = ERFOLGE.includes(ergebnis);
      ziel.textContent = this.getAttribute(`data-text-${ergebnis}`) ?? '';
      ziel.classList.toggle('text-error', !erfolg);
      ziel.classList.toggle('text-success', erfolg);
      ziel.hidden = ziel.textContent === '';
    }
  }

  class MmKartenfeld extends MmBasis {
    /** @type {((e: Event) => void) | undefined} */
    gleiche;

    /** @type {MutationObserver | undefined} */
    beobachter;

    connectedCallback() {
      this.verbinde({
        pruefen: () => this.pruefe(),
        enter: () => this.pruefe(),
        entfernen: () => this.entferne(),
        andere: () => {
          this.zeige('eingabe');
          this.eingabe?.focus();
        },
        abbrechen: () => this.zurueckZumKonto(),
        aenderung: (ziel) => {
          if (ziel?.matches('[data-mm-speichern]')) this.merkeSpeichern(/** @type {HTMLInputElement} */ (ziel).checked);
        },
      });
      // Andere Kartenfelder derselben Seite (z. B. Warenkorbseite und Drawer) gleichziehen
      this.gleiche = (/** @type {Event} */ e) => {
        const d = /** @type {CustomEvent<{ quelle: Element, maskiert: string | null, aktiv: boolean }>} */ (e).detail;
        if (d.quelle === this) return;
        if (d.maskiert) {
          this.querySelectorAll('[data-mm-maskiert]').forEach((el) => {
            el.textContent = d.maskiert;
          });
          this.zeige('fertig');
        } else {
          this.zeige(this.ohneWarenkorbKarte);
        }
        this.aktiv = d.aktiv || this.hasAttribute('data-mm-gespeichert');
      };
      document.addEventListener('mm:karte', this.gleiche);

      // Drawer geschlossen (Prestige: <cart-drawer id="cart-drawer"> verliert „open“): offenes Feld nach
      // „Andere Nummer“ zurücksetzen, sonst steht es beim nächsten Öffnen noch da (Christian 08.10.)
      const drawer = this.closest('#cart-drawer');
      if (drawer && !this.beobachter) {
        this.beobachter = new MutationObserver(() => {
          if (!drawer.hasAttribute('open')) this.zurueckZumKonto();
        });
        this.beobachter.observe(drawer, { attributes: true, attributeFilter: ['open'] });
      }

      this.ladeAusWarenkorb();
    }

    /**
     * Nummer aus dem Warenkorb übernehmen, wenn das Feld ohne sie gerendert wurde: Sonst stünde nach jedem
     * Seitenwechsel wieder das leere Feld da, obwohl die Nummer im Warenkorb liegt und im Checkout ankommt.
     */
    async ladeAusWarenkorb() {
      if (this.hatKarte) return;
      try {
        const wurzel = window.Shopify?.routes?.root ?? '/';
        const antwort = await fetch(`${wurzel}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (!antwort.ok) return;
        const stand = karteImWarenkorb(await antwort.json());
        // Inzwischen selbst geprüft oder eine Eingabe begonnen: nichts überschreiben
        if (!stand || this.hatKarte || this.beschaeftigt || this.eingabe?.value) return;
        this.zeigeKarte(stand.karte);
        const haken = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-mm-speichern]'));
        if (haken) haken.checked = stand.speichern;
        this.aktiv = true;
        // Im Warenkorb ist das Feld mit Nummer aufgeklappt, wie es Liquid mit Nummer rendern würde
        if (this.dataset.mmOrt === 'drawer' || this.dataset.mmOrt === 'seite') this.querySelector('details')?.setAttribute('open', '');
      } catch {
        // Anzeige bleibt wie gerendert; die Nummer liegt trotzdem im Warenkorb
      }
    }

    disconnectedCallback() {
      if (this.gleiche) document.removeEventListener('mm:karte', this.gleiche);
      this.beobachter?.disconnect();
      this.beobachter = undefined;
    }

    /** Zurück zur gespeicherten Karte, solange keine neue Nummer im Warenkorb steht (Abbrechen, Drawer zu) */
    zurueckZumKonto() {
      if (this.hatKarte || !this.hasAttribute('data-mm-gespeichert')) return;
      if (this.eingabe) this.eingabe.value = '';
      const haken = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-mm-speichern]'));
      if (haken) haken.checked = false;
      const meldung = /** @type {HTMLElement | null} */ (this.querySelector('[data-mm-meldung]'));
      if (meldung) meldung.hidden = true;
      this.zeige('konto');
    }

    /** Nummer hinterlegt (Warenkorb oder Kundenkonto): blendet data-mm-nur-aktiv/-inaktiv per CSS um */
    set aktiv(ja) {
      this.toggleAttribute('data-mm-aktiv', ja);
    }

    /** Leiste und Produktseite klappen nach dem Prüfen zu, der Kopf zeigt dann „✓ Servicekarte •••• 1933“ */
    klappeZu() {
      if (!this.hasAttribute('data-mm-zuklappen')) return;
      const klapp = this.querySelector('details');
      if (klapp) klapp.open = false;
    }

    /** @param {string | null} maskiert */
    meldeAnAndere(maskiert) {
      document.dispatchEvent(
        new CustomEvent('mm:karte', { detail: { quelle: this, maskiert, aktiv: maskiert !== null || this.hasAttribute('data-mm-gespeichert') } }),
      );
    }

    get speichern() {
      return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-mm-speichern]'))?.checked === true;
    }

    get hatKarte() {
      return this.querySelector('[data-mm-ansicht="fertig"]:not([hidden])') !== null;
    }

    /** Ansicht ohne Nummer im Warenkorb: die im Kundenkonto gespeicherte, sonst das Feld */
    get ohneWarenkorbKarte() {
      return this.hasAttribute('data-mm-gespeichert') ? 'konto' : 'eingabe';
    }

    /** Leiste und Produktseite: Drawer des Themes neu laden, er wurde ohne die Nummer gerendert. */
    aktualisiereDrawer() {
      if (this.hasAttribute('data-mm-drawer-neu')) document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    async pruefe() {
      if (this.beschaeftigt) return;
      const karte = normiereKarte(this.eingabe?.value);
      if (!karte) return this.melde('format');

      this.beschaeftigt = true;
      try {
        const ergebnis = await rufeProxy('pruefen', { karte });
        if (ergebnis === 'gueltig' || ergebnis === 'nicht_erreichbar') {
          await setzeAttribute({ [ATTRIBUT_KARTE]: karte, [ATTRIBUT_SPEICHERN]: this.speichern ? 'ja' : '' });
          this.zeigeKarte(karte);
          this.aktiv = true;
          this.melde(ergebnis === 'gueltig' ? 'gueltig' : 'ungeprueft');
          this.klappeZu();
          this.meldeAnAndere(maskiereKarte(karte));
          this.aktualisiereDrawer();
        } else {
          this.melde(ergebnis);
        }
      } catch {
        this.melde('fehler');
      } finally {
        this.beschaeftigt = false;
      }
    }

    async entferne() {
      if (this.beschaeftigt) return;
      this.beschaeftigt = true;
      try {
        await setzeAttribute({ [ATTRIBUT_KARTE]: '', [ATTRIBUT_SPEICHERN]: '' });
        const haken = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-mm-speichern]'));
        if (haken) haken.checked = false;
        this.zeige(this.ohneWarenkorbKarte);
        this.aktiv = this.hasAttribute('data-mm-gespeichert');
        this.melde('entfernt');
        if (!this.hasAttribute('data-mm-gespeichert')) this.eingabe?.focus();
        this.meldeAnAndere(null);
        this.aktualisiereDrawer();
      } catch {
        this.melde('fehler');
      } finally {
        this.beschaeftigt = false;
      }
    }

    /** Häkchen nachträglich gesetzt oder entfernt: nur relevant, wenn schon eine Nummer im Warenkorb steht. */
    async merkeSpeichern(/** @type {boolean} */ ja) {
      if (!this.hatKarte) return;
      try {
        await setzeAttribute({ [ATTRIBUT_SPEICHERN]: ja ? 'ja' : '' });
      } catch {
        this.melde('fehler');
      }
    }
  }

  class MmKundenkonto extends MmBasis {
    connectedCallback() {
      this.verbinde({
        speichern: () => this.speichere(),
        enter: () => this.speichere(),
        loeschen: () => this.loesche(),
      });
    }

    async speichere() {
      if (this.beschaeftigt) return;
      const karte = normiereKarte(this.eingabe?.value);
      if (!karte) return this.melde('format');

      this.beschaeftigt = true;
      try {
        const ergebnis = await rufeProxy('speichern', { karte });
        if (ergebnis === 'gespeichert') this.zeigeKarte(karte);
        this.melde(ergebnis);
      } finally {
        this.beschaeftigt = false;
      }
    }

    async loesche() {
      if (this.beschaeftigt) return;
      this.beschaeftigt = true;
      try {
        const ergebnis = await rufeProxy('loeschen', {});
        if (ergebnis === 'geloescht') this.zeige('eingabe');
        this.melde(ergebnis);
      } finally {
        this.beschaeftigt = false;
      }
    }
  }

  if (!customElements.get('mm-kartenfeld')) customElements.define('mm-kartenfeld', MmKartenfeld);
  if (!customElements.get('mm-kundenkonto')) customElements.define('mm-kundenkonto', MmKundenkonto);
}

// In den Tests (Node) gibt es kein DOM; dort werden nur die Funktionen oben geprüft.
if (typeof window !== 'undefined' && typeof customElements !== 'undefined') definiereElemente();
